import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n';
import { Modal } from './Modal';
import { GameStatsCard } from './GameStatsCard';
import { useToast } from './ToastContext';
import { AppIcon } from './icons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

interface GameStatisticsData {
  gameId: string;
  roomName: string;
  gameType: string;
  playedAt: number;
  leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
  statistics: {
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
    hotTakesStats?: {
      totalQuestions: number;
      totalTies: number;
      mostDrinks: { playerId: string; playerName: string; count: number } | null;
      mostControversial: { statement: string; agreeCount: number; disagreeCount: number } | null;
      playerDrinks: Array<{ playerId: string; playerName: string; count: number }>;
    };
    hotTakesResults?: Array<{
      questionId: string;
      statement: string;
      agreeCount: number;
      disagreeCount: number;
      minorityVote: 'AGREE' | 'DISAGREE' | 'tie';
      debatePrompt?: string;
      votes: Array<{
        playerId: string;
        playerName: string;
        avatar?: string;
        vote: 'AGREE' | 'DISAGREE';
        isMinority: boolean;
        mustDrink: boolean;
      }>;
    }>;
  };
  players: Array<{ id: string; name: string; avatar?: string }>;
}

interface RoomStatisticsResponse {
  roomName: string;
  games: GameStatisticsData[];
  totalGames: number;
}

interface StatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomName: string;
  getPlayerAvatar?: (playerId: string) => string | undefined;
}

export const StatisticsModal: React.FC<StatisticsModalProps> = ({
  isOpen,
  onClose,
  roomName,
  getPlayerAvatar,
}) => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<RoomStatisticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (isOpen && roomName) {
      fetchStatistics();
    } else {
      // Reset state when modal closes
      setStatistics(null);
      setError(null);
    }
  }, [isOpen, roomName]);

  const fetchStatistics = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/${roomName}/statistics`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(t('lobby.statistics.noStats'));
          setStatistics({ roomName, games: [], totalGames: 0 });
        } else {
          throw new Error(`Failed to fetch statistics: ${response.statusText}`);
        }
      } else {
        const data: RoomStatisticsResponse = await response.json();
        setStatistics(data);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('lobby.statistics.loading');
      setError(errorMessage);
      showToast(errorMessage, 'error');
      console.error('Error fetching statistics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      contentStyle={{
        maxWidth: '900px',
        width: '95%',
        maxHeight: '90vh',
        padding: '2rem',
      }}
    >
      <div style={{ width: '100%' }}>
        <h2 style={{
          color: 'var(--text-primary)',
          fontSize: '1.8rem',
          marginBottom: '1.5rem',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
        }}>
          <AppIcon name="barChart" sx={{ color: 'var(--color-primary)' }} />
          <span>{t('lobby.statistics.title')}</span>
        </h2>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--color-primary)' }}>
              <AppIcon name="hourglass" fontSize="large" />
            </div>
            <div>{t('lobby.statistics.loading')}</div>
          </div>
        )}

        {error && !isLoading && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: 'var(--color-error)',
            background: 'rgba(239, 68, 68, 0.08)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        {!isLoading && statistics && (
          <>
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--bg-tint)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-accent)',
              textAlign: 'center',
            }}>
              <div style={{ color: 'var(--color-primary)', fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {t('lobby.statistics.roomLabel', { roomName: statistics.roomName })}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                {t('lobby.statistics.gamesPlayed', {
                  count: statistics.totalGames,
                  gameLabel: statistics.totalGames === 1 ? t('common.game_one') : t('common.game_other'),
                })}
              </div>
            </div>

            {statistics.games.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: 'var(--text-secondary)',
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem', color: 'var(--color-primary)', opacity: 0.7 }}>
                  <AppIcon name="inbox" sx={{ fontSize: '3rem' }} />
                </div>
                <div style={{ fontSize: '1.1rem', color: 'var(--text-primary)', fontWeight: 600 }}>{t('lobby.statistics.emptyTitle')}</div>
                <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                  {t('lobby.statistics.emptyHint')}
                </div>
              </div>
            ) : (
              <div style={{
                maxHeight: '60vh',
                overflowY: 'auto',
                paddingRight: '0.5rem',
              }}>
                {statistics.games.map((game) => (
                  <GameStatsCard
                    key={game.gameId}
                    game={game}
                    getPlayerAvatar={getPlayerAvatar}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default StatisticsModal;
